import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Play, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { attachHls } from "@/components/video/VideoPlayer";

/** Vídeo em ecrã cheio para o Feed: só reproduz quando `active`; som controlado pelo pai (muted). */
export function FeedVideo({ video, active, muted, onTap, testid }) {
  const ref = useRef(null);
  const hlsRef = useRef(null);
  const retried = useRef(false);
  const [src, setSrc] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | ready | blocked | error
  const [needsTap, setNeedsTap] = useState(false);
  const [paused, setPaused] = useState(false);
  const portrait = video?.width && video?.height ? video.height >= video.width : false;

  useEffect(() => {
    if (!active || src || state !== "idle" || !video?.id) return;
    setState("loading");
    api.post(`/videos/${video.id}/playback`).then((r) => { setSrc(r.data); setState("ready"); })
      .catch((e) => setState(e?.response?.status === 403 ? "blocked" : "error"));
  }, [active, src, state, video?.id]);

  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    v.defaultMuted = true;
    const expire = () => {
      const expired = src.expires_at && Date.now() / 1000 >= src.expires_at - 5;
      if (expired || !retried.current) { retried.current = !expired; hlsRef.current?.destroy(); hlsRef.current = null; setSrc(null); setState("idle"); }
      else setState("error");
    };
    v.onerror = expire;
    const h = attachHls(v, src.hls);
    if (h) { hlsRef.current = h; h.on(Hls.Events.ERROR, (_, d) => d.fatal && expire()); }
    return () => { hlsRef.current?.destroy(); hlsRef.current = null; v.onerror = null; };
  }, [src]);

  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    v.muted = muted;
    if (active && !paused) v.play().then(() => setNeedsTap(false)).catch(() => setNeedsTap(true));
    else { v.pause(); if (!active) { v.currentTime = 0; setPaused(false); } }
  }, [active, src, muted, paused]);

  useEffect(() => {
    const onVis = () => { if (document.hidden) ref.current?.pause(); else if (active && !paused && src) ref.current?.play().catch(() => {}); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [active, paused, src]);

  const tap = () => { if (needsTap) { setNeedsTap(false); ref.current?.play().catch(() => setNeedsTap(true)); return; } setPaused((p) => !p); onTap?.(); };

  if (state === "blocked") return <div data-testid={`${testid}-blocked`} className="absolute inset-0 flex items-center justify-center text-white/80 text-sm gap-2 px-8 text-center"><EyeOff size={18} /> Vídeo indisponível para a tua conta.</div>;
  if (state === "error") return <div data-testid={`${testid}-error`} className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">Vídeo indisponível.</div>;

  return (
    <div className="absolute inset-0" onClick={tap} data-testid={testid} data-active={active} data-paused={paused}>
      {src?.thumbnail && !portrait && <img src={src.thumbnail} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60" />}
      <video ref={ref} data-testid={`${testid}-el`} playsInline muted loop preload="metadata" poster={src?.thumbnail}
             className={`absolute inset-0 w-full h-full ${portrait ? "object-cover" : "object-contain"}`} />
      {(!src || needsTap || paused) && (
        <div data-testid={`${testid}-tap`} className="absolute inset-0 flex items-center justify-center text-white/90 pointer-events-none">
          <span className="bg-black/40 backdrop-blur rounded-full p-4"><Play size={34} fill="currentColor" /></span>
        </div>
      )}
    </div>
  );
}
